// AGC: WarehouseSelector Component
// AGC: Dropdown filter for warehouse selection — only shown when 2+ warehouses exist.
// AGC: Options: All (default), individual warehouses, Grouped (all items grouped by warehouse).
// AGC: Includes gear icon to open ManageWarehousesModal.

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { ChevronDown, Warehouse, Layers, Settings } from 'lucide-react'
import type { Warehouse as WarehouseType } from '../../types/inventory'
import type { WarehouseFilter } from '../../hooks/useWarehouse'

interface WarehouseSelectorProps {
  warehouses: WarehouseType[]
  selected: WarehouseFilter
  onChange: (filter: WarehouseFilter) => void
  onManage?: () => void  // AGC: Open ManageWarehousesModal
}

export const WarehouseSelector: React.FC<WarehouseSelectorProps> = ({
  warehouses,
  selected,
  onChange,
  onManage,
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // AGC: Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const getLabel = useCallback((): string => {
    if (selected === 'all') return 'All Warehouses'
    if (selected === 'grouped') return 'Grouped by Warehouse'
    const wh = warehouses.find(w => w.id === selected)
    return wh?.name || 'Select Warehouse'
  }, [selected, warehouses])

  const handleSelect = useCallback((filter: WarehouseFilter) => {
    onChange(filter)
    setOpen(false)
  }, [onChange])

  return (
    <div ref={ref} className="relative">
      {/* AGC: Trigger button */}
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
      >
        <Warehouse className="w-4 h-4 text-gray-500" />
        <span className="max-w-[140px] truncate">{getLabel()}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* AGC: Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-1 z-[60] w-56 rounded-xl border border-gray-200 bg-white shadow-lg py-1">
          {/* AGC: All option */}
          <button
            onClick={() => handleSelect('all')}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${selected === 'all' ? 'text-green-700 font-semibold' : 'text-gray-700'}`}
          >
            <Warehouse className="w-4 h-4 text-gray-400" />
            <span>All Warehouses</span>
            {selected === 'all' && <span className="ml-auto text-green-600">✓</span>}
          </button>

          {/* AGC: Individual warehouses */}
          {warehouses.length > 0 && (
            <>
              <div className="mx-3 my-1 border-t border-gray-100" />
              {warehouses.map(wh => (
                <button
                  key={wh.id}
                  onClick={() => handleSelect(wh.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${selected === wh.id ? 'text-green-700 font-semibold' : 'text-gray-700'}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />
                  <span className="truncate">{wh.name}</span>
                  {wh.isDefault && <span className="ml-auto text-xs text-gray-400">default</span>}
                  {selected === wh.id && !wh.isDefault && <span className="ml-auto text-green-600">✓</span>}
                </button>
              ))}
            </>
          )}

          {/* AGC: Grouped option */}
          <div className="mx-3 my-1 border-t border-gray-100" />
          <button
            onClick={() => handleSelect('grouped')}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${selected === 'grouped' ? 'text-green-700 font-semibold' : 'text-gray-700'}`}
          >
            <Layers className="w-4 h-4 text-gray-400" />
            <span>Grouped by Warehouse</span>
            {selected === 'grouped' && <span className="ml-auto text-green-600">✓</span>}
          </button>

          {/* AGC: Manage warehouses */}
          {onManage && (
            <>
              <div className="mx-3 my-1 border-t border-gray-100" />
              <button
                onClick={() => { setOpen(false); onManage() }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Manage Warehouses</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default WarehouseSelector
